import React from "react";
import { AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { isSafeUrl } from "@/lib/companyProfile";

/* Kerangka satu section: judul + deskripsi + toggle aktif. Konten tetap bisa diedit saat nonaktif (tersimpan). */
export function SectionShell({ title, description, enabled, onToggle, children, testId }) {
  return (
    <Card className="p-5 space-y-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base md:text-lg font-bold">{title}</h2>
          {description ? <p className="text-xs text-[#8A8A8A] mt-0.5 leading-relaxed">{description}</p> : null}
        </div>
        {onToggle ? (
          <label className="flex items-center gap-2 text-xs font-semibold shrink-0">
            <span className={enabled ? "text-emerald-700" : "text-[#8A8A8A]"}>{enabled ? "Tampil" : "Disembunyikan"}</span>
            <Switch checked={!!enabled} onCheckedChange={onToggle} data-testid={`${testId}-enabled`} />
          </label>
        ) : null}
      </div>
      {onToggle && !enabled ? (
        <div className="text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 flex gap-2" data-testid={`${testId}-hidden-note`}>
          <AlertCircle size={14} className="shrink-0 mt-0.5" /> Section disembunyikan dari halaman depan. Konten yang tersimpan tidak dihapus.
        </div>
      ) : null}
      <div className="grid gap-4">{children}</div>
    </Card>
  );
}

export function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs font-medium">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint ? <p className="text-[11px] text-[#8A8A8A] mt-1">{hint}</p> : null}
    </div>
  );
}

export function TextField({ label, value, onChange, maxLength = 120, placeholder, testId, hint }) {
  return (
    <Field label={label} hint={hint}>
      <Input value={value || ""} maxLength={maxLength} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </Field>
  );
}

export function AreaField({ label, value, onChange, maxLength = 2000, placeholder, testId, rows = 4, hint }) {
  return (
    <Field label={label} hint={hint}>
      <Textarea value={value || ""} maxLength={maxLength} rows={rows} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </Field>
  );
}

/* URL: http(s)://, mailto:, tel:, atau path internal /… — selain itu ditandai (backend juga menolak). */
export function UrlField({ label, value, onChange, placeholder = "https://… atau /katalog", testId, hint }) {
  const invalid = String(value || "").trim() && !isSafeUrl(value);
  return (
    <Field label={label} hint={hint}>
      <Input value={value || ""} maxLength={500} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} aria-invalid={invalid ? "true" : undefined} className={invalid ? "border-red-400" : ""} />
      {invalid ? <p className="text-[11px] text-red-600 mt-1 font-semibold" data-testid={`${testId}-error`}>URL harus diawali https://, http://, mailto:, tel:, atau / (tautan internal).</p> : null}
    </Field>
  );
}
